import React, { MouseEventHandler, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router';

import { PublicPage } from 'api/website';
import { getContentLocaleUrl, getHideFooterDivider } from 'cms/helpers';
import {
  useContentDefaultSearchType,
  useContentHasPostSearchToolbar,
} from 'cms/hooks';
import { SearchToolbar } from 'components/Search/SearchToolbar';
import { Layout } from 'layouts/Layout';
import { isDefined } from 'utils/functional';
import { setDocumentTitle } from 'utils/meta';
import { scrollToHash } from 'utils/scrollIntoView';
import { getParam, hasParam } from 'utils/url';

import { ContentPageWrapper } from './ContentPageWrapper';

type Props = {
  content: PublicPage;
};

export function ContentPage({ content }: Props) {
  const location = useLocation();

  const onClickContent: MouseEventHandler<HTMLElement> = useCallback((e) => {
    const el = e.target as HTMLElement;
    const href = el.getAttribute('href');

    if (el.nodeName === 'A' && /^#/.test(href)) {
      e.preventDefault();
      scrollToHash(href);
    }
  }, []);

  useEffect(() => {
    setDocumentTitle(content.headTitle || content.title);
  }, [content.headTitle, content.title]);

  useEffect(() => {
    if (hasParam(location.search, 'block')) {
      const el = document.getElementById(
        `cms-block-${getParam(location, 'block')}`
      );

      if (el) {
        window.scrollBy(0, el.getBoundingClientRect().top - 200);
      }
    }
  }, [location]);

  useEffect(() => {
    if (window.location.hash) {
      window.setTimeout(() => {
        scrollToHash(window.location.hash.split('?')[0], {
          padding: 32,
          noTransition: true,
        });
      }, 1);
    }
  }, []);

  const hero =
    content.heroSearch && content.heroSearch.onlyResults
      ? undefined
      : content.heroSearch;

  const defaultSearchType = useContentDefaultSearchType({ content });
  const hideFooterDivider = getHideFooterDivider({ content });
  const hasPostSearchToolbar = useContentHasPostSearchToolbar({ content });
  const localeUrl = getContentLocaleUrl({ content });

  return (
    <Layout
      content={content}
      hero={hero}
      defaultWithSearchHero={isDefined(content.heroSearch)}
      showListingCounts={
        !!content.heroSearch && !!content.heroSearch.showCounts
      }
      navSection="MAIN"
      searchMode={false}
      postSearchToolbar={
        hasPostSearchToolbar ? <SearchToolbar content={content} /> : undefined
      }
      defaultSearchType={defaultSearchType}
      hideFooterDivider={hideFooterDivider}
      localeUrl={localeUrl}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={onClickContent}>
        <ContentPageWrapper content={content} />
      </div>
    </Layout>
  );
}
